
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { getRelatedPosts } from '../../../mocks/blogPosts';

interface RelatedPostsProps {
  currentPostId: number;
}

export default function RelatedPosts({ currentPostId }: RelatedPostsProps) {
  const { ref, isVisible } = useScrollAnimation();
  const relatedPosts = getRelatedPosts(currentPostId);

  if (relatedPosts.length === 0) return null;

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Social Media': 'bg-pink-100 text-pink-700',
      'Content Strategy': 'bg-emerald-100 text-emerald-700',
      SEO: 'bg-sky-100 text-sky-700',
      'Paid Ads': 'bg-amber-100 text-amber-700',
      Analytics: 'bg-violet-100 text-violet-700',
      Branding: 'bg-rose-100 text-rose-700',
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  return (
    <section className="py-16 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-12">
            <h2 className="font-['Montserrat'] text-2xl md:text-3xl font-bold text-slate-800 mb-3">
              Related Articles
            </h2>
            <p className="text-slate-600">Continue reading with these related posts</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {relatedPosts.map((post, index) => (
              post && (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 cursor-pointer ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getCategoryColor(post.category)}`}>
                        {post.category}
                      </span>
                      <span className="text-slate-400 text-xs">{post.readTime} read</span>
                    </div>

                    <h3 className="font-['Montserrat'] text-lg font-bold text-slate-800 mb-3 line-clamp-2 group-hover:text-[#00989e] transition-colors">
                      {post.title}
                    </h3>

                    <p className="text-slate-600 text-sm line-clamp-2">{post.excerpt}</p>
                  </div>
                </Link>
              )
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-[#00989e] text-[#00989e] font-semibold rounded-full hover:bg-[#00989e] hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              View All Articles
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
